<template>
  <div id="hybrid_settings">
    <div id="main_page">
      <div id="logo"></div>
      <div id="mode_select_layout" v-if="showModeSelect">
        <div class="userProfile" v-if="hybridSettings.user">
          <div class="userPhotoHolder">
            <div class="userPhoto">
              <img
                :src="
                  hybridSettings.user.profile_image
                    ? hybridSettings.user.profile_image.thumbnail_url
                    : emptyProfileURL
                "
                loading="eager"
              />
            </div>
          </div>
          <div class="userName">{{ hybridSettings.user.name }}</div>
        </div>
        <div class="modeSelect">
          <div
            class="modeOption publicStream"
            :class="{ selected: !isPremium }"
            @click="clickedModeOption(false)"
          >
            <div class="modeOptionIcon"></div>
            <div class="modeOptionText">
              {{ $t("hybrid_settings.public_stream") }}
            </div>
          </div>
          <div
            class="modeOption privateStream"
            :class="{ selected: isPremium }"
            @click="clickedModeOption(true)"
          >
            <div class="modeOptionIcon"></div>
            <div class="modeOptionText">
              {{ $t("hybrid_settings.private_stream") }}
            </div>
          </div>
        </div>
        <div class="modeSelectGift" v-if="isPremium">
          <div class="giftTitle">
            {{ $t("hybrid_settings.selected_gift") }}
          </div>
          <div class="giftWrapper">
            <img
              class="giftImage"
              v-if="selectedGift"
              :src="selectedGift.image"
            />
            <div class="giftPrice" v-if="selectedGift">
              <img src="@/assets/img/coin.svg" />
              <span> {{ selectedGift.cost }} </span>
            </div>
            <div class="spacer"></div>
            <div class="changeButton" @click="changeGiftButton()">
              {{ $t("hybrid_settings.change_gift") }}
            </div>
          </div>
        </div>
        <div class="modeSelectInvites" v-if="isPremium">
          <div class="invitesTitle">
            {{ $t("hybrid_settings.selected_invites") }}
          </div>
          <div class="invitesWrapper">
            <div class="inviteImages" v-if="selectedInvites.length > 0">
              <div class="inviteImage">
                <img
                  :src="
                    selectedInvites[0].user.profile_image
                      ? selectedInvites[0].user.profile_image.thumbnail_url
                      : emptyProfileURL
                  "
                />
              </div>
              <div class="inviteImage" v-if="selectedInvites.length > 1">
                <img
                  :src="
                    selectedInvites[1].user.profile_image
                      ? selectedInvites[1].user.profile_image.thumbnail_url
                      : emptyProfileURL
                  "
                />
              </div>
              <div class="inviteImage" v-if="selectedInvites.length > 2">
                <img
                  :src="
                    selectedInvites[2].user.profile_image
                      ? selectedInvites[2].user.profile_image.thumbnail_url
                      : emptyProfileURL
                  "
                />
              </div>
            </div>
            <div class="extraInviteCount">
              <span v-if="selectedInvites.length == 0">
                0 {{ $t("hybrid_settings.extra_invited") }}
              </span>
              <span v-if="selectedInvites.length > 3">
                +{{ selectedInvites.length - 3 }}
                {{ $t("hybrid_settings.extra_invited") }}
              </span>
            </div>
            <div class="spacer"></div>
            <div class="changeButton" @click="changeInvitesButton()">
              {{ $t("hybrid_settings.change_gift") }}
            </div>
          </div>
        </div>
        <div class="modeSelectGiftPlaceholder" v-else></div>
        <div class="modeSelectInfo">
          <img src="@/assets/img/hybrid_settings/info.svg" />
          <div class="modeSelectInfoText">
            {{
              isPremium
                ? $t("hybrid_settings.next_stream_private")
                : $t("hybrid_settings.next_stream_public")
            }}
          </div>
        </div>
        <div class="modeSaveBox">
          <div class="modeSaveButton" @click="saveHybridSettings()">
            {{ $t("hybrid_settings.save_button") }}
          </div>
        </div>
      </div>
    </div>

    <GiftSelectModal
      v-model="showGiftModal"
      :gifts="hybridSettings.start_gifts"
      :continueMode="!showModeSelect"
      @done="giftSelectModalDone"
    />

    <InviteModal
      v-model="showInviteModal"
      :gifters="inviteGifters"
      :goLiveMode="!showModeSelect"
      :canLoadMore="!!inviteCursor"
      :loading="loadingInvites"
      @done="inviteModalDone"
      @loadMore="inviteModalLoadMore"
    />

    <SuccessModal
      v-model="showSuccessModal"
      :livestreamStarted="!showModeSelect"
      :isPremium="isPremium"
      @done="successModalDone()"
    />

    <b-loading
      v-if="loading"
      :active="true"
      :can-cancel="false"
      class="dark-loading"
    ></b-loading>

    <div id="back_button" @click="backButtonPressed()">
      <img src="@/assets/img/hybrid_settings/back.svg" />
    </div>
  </div>
</template>

<script>
import _ from "lodash";

import GiftSelectModal from "@/components/HybridSettings/GiftSelectModal.vue";
import InviteModal from "@/components/HybridSettings/InviteModal.vue";
import SuccessModal from "@/components/HybridSettings/SuccessModal.vue";
import emptyProfileURL from "@/assets/img/empty_profile.png";

// Transparent modal style hack without breaking rest of the app.
let styleEl = null;
function removeStyle() {
  if (!styleEl) return;
  styleEl.parentNode.removeChild(styleEl);
  styleEl = null;
}
function injectStyle(hideBackground) {
  removeStyle();
  styleEl = document.createElement("style");
  if (hideBackground) {
    styleEl.textContent = `.v-overlay { display: none; }`;
  } else {
    styleEl.textContent = `.v-overlay--active > .v-overlay__scrim { opacity: 0.8 !important; background: #000 !important; }`;
  }
  document.head.appendChild(styleEl);
}

// Disable zoom on this page.
let originalViewPortContent = null;
function disableZoom() {
  let viewport = document.querySelector('meta[name="viewport"]');
  if (viewport) {
    originalViewPortContent = viewport.getAttribute("content");
    viewport.setAttribute(
      "content",
      "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover"
    );
  }
}
function resetZoom() {
  let viewport = document.querySelector('meta[name="viewport"]');
  if (originalViewPortContent && viewport) {
    viewport.setAttribute("content", originalViewPortContent);
  }
}

let invitePromise = Promise.resolve();
let invitePromiseController = new AbortController();
let invitePromiseInitialFail = false;

export default {
  name: "HybridSettings",
  components: {
    GiftSelectModal,
    InviteModal,
    SuccessModal,
  },
  data() {
    return {
      emptyProfileURL,
      loading: true,
      loadingInvites: false,
      //
      hybridSettings: {
        user: null,
        gift_id: null,
        active_livestream_id: null,
        start_gifts: [],
      },
      inviteGifters: [],
      inviteCursor: null,
      //
      showModeSelect: false,
      isPremium: false,
      selectedGift: null,
      //
      showGiftModal: false,
      showInviteModal: false,
      showSuccessModal: false,
    };
  },
  computed: {
    selectedInvites() {
      return this.inviteGifters.filter((it) => it.selected);
    },
  },
  created() {
    this.$hybridapi("updateWindowSize", "full");
    this.$hybridapi("updateTopBar", "hide");

    // Duplicate window bug fix.
    window.addEventListener("storage", (event) => {
      if (event.key === "hybridSettingsClosedAt") {
        console.log("Other window closed:", event);
        this.$hybridapi("close", "close");
      }
    });

    this.getHybridSettings().then(() => {
      if (this.showModeSelect) {
        this.$hybridapi(
          "updatePageTitle",
          this.$t("hybrid_settings.make_livestream_private")
        );
      } else {
        this.$hybridapi(
          "updatePageTitle",
          this.$t("hybrid_settings.start_private_livestream")
        );
      }
      this.$hybridapi("hideBackButton", "hide");
      this.$hybridapi("hideLoading");

      if (this.showModeSelect) {
        this.preloadImageURLs(
          this.hybridSettings.start_gifts.slice(0, 8).map((gift) => gift.image)
        );
      }

      disableZoom();

      // TEST CODE
      // this.toggleGiftModal(true);
      // this.toggleInviteModal(true);
      // this.toggleSuccessModal(true);
    });
  },
  beforeDestroy() {
    removeStyle();
    resetZoom();
  },
  methods: {
    close() {
      try {
        localStorage.hybridSettingsClosedAt = new Date().toJSON();
      } catch (error) {
        console.error("hybridSettingsClosedAt set error", error);
      }
      this.$hybridapi("close", "close");
    },
    preloadImageURLs(urls) {
      const preloadPromises = urls.map((url) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.src = url;
          img.onload = () => resolve();
          img.onerror = () => resolve();
        });
      });
      return Promise.all(preloadPromises);
    },
    clickedModeOption(isPremium) {
      this.isPremium = isPremium;
      if (isPremium && !this.selectedGift) {
        this.toggleGiftModal(true);
      }
    },
    toggleGiftModal(show) {
      this.updateModalStyle(!this.showModeSelect);
      this.showGiftModal = show;
    },
    async toggleInviteModal(show) {
      this.updateModalStyle(!this.showModeSelect);
      if (show) {
        this.loading = true;

        // Wait for list
        await invitePromise;

        // Retry if fail
        if (invitePromiseInitialFail) {
          await this.getInvites();
        }

        // Reload page if fail again
        if (invitePromiseInitialFail) {
          console.error("failed to get invite list");
          this.$hybridapi("showToast", this.$t("errors.general_error"));
          this.getHybridSettings();
          return;
        }

        if (this.showModeSelect) {
          this.$nextTick(() => {
            this.loading = false;
            this.showInviteModal = true;
          });
        } else {
          this.$nextTick(() => {
            if (this.inviteGifters.length == 0) {
              console.log("no one to invite. skipping step.");
              this.makeLivestreamPrivate([]);
            } else {
              this.loading = false;
              this.showInviteModal = true;
            }
          });
        }
      } else {
        this.showInviteModal = false;
      }
    },
    toggleSuccessModal(show) {
      this.updateModalStyle(false);
      this.showSuccessModal = show;
    },
    updateModalStyle(hideBackground) {
      injectStyle(hideBackground);
    },
    getHybridSettings() {
      invitePromiseController.abort();
      this.loading = true;
      return this.$axios.post("/user/hybrid_livestream_settings").then(
        (response) => {
          const hybridSettings = response.data;
          console.log("hybrid_livestream_settings", hybridSettings);

          if (!hybridSettings.user) {
            console.error("hybrid_livestream_settings no user", response);
            this.close();
            return;
          }

          // TEST CODE //
          // hybridSettings.active_livestream_id = 1;
          // hybridSettings.already_private = true;
          // hybridSettings.user.profile_image = null;

          this.hybridSettings = hybridSettings;

          if (hybridSettings.gift_id) {
            this.isPremium = true;
            this.selectedGift =
              _.find(hybridSettings.start_gifts, (gift) => {
                return gift.gift_id == hybridSettings.gift_id;
              }) || null;
          } else {
            this.isPremium = false;
            this.selectedGift = null;
          }

          // clean invite data
          invitePromiseController = new AbortController();
          invitePromise = Promise.resolve();
          invitePromiseInitialFail = false;
          this.inviteGifters = [];
          this.inviteCursor = null;

          if (hybridSettings.active_livestream_id == null) {
            // no active livestream
            this.showModeSelect = true;

            hybridSettings.allowed_users.forEach((item) => {
              item.selected = true;
            });
            this.inviteGifters = hybridSettings.allowed_users;

            this.getInvites();
          } else if (hybridSettings.already_private) {
            // active livestream but already private
            console.error(
              "already_private case",
              hybridSettings.active_livestream_id
            );
            this.showModeSelect = false;
            this.isPremium = true;
            this.toggleSuccessModal(true);
            setTimeout(() => {
              this.close();
            }, 2000);
          } else {
            // active non-private livestream
            this.showModeSelect = false;
            this.toggleGiftModal(true);

            this.getInvites();
          }

          this.loading = false;
        },
        (error) => {
          console.error("hybrid_livestream_settings error", error);
          this.$hybridapi("showToast", this.$t("errors.general_error"));
          this.close();
          throw error;
        }
      );
    },
    saveHybridSettings() {
      let giftId = null;
      let allowed_ids = [];
      if (this.isPremium && this.selectedGift) {
        giftId = this.selectedGift.gift_id;
        allowed_ids = this.inviteGifters
          .filter((it) => it.selected)
          .map((it) => it.user.user_id);
      }

      this.loading = true;
      this.$axios
        .post("/user/set_hybrid_livestream_settings", {
          gift_id: giftId,
          allowed_ids: allowed_ids,
        })
        .then(
          (response) => {
            console.log("set_hybrid_livestream_settings", response.data);

            if (response.data.success) {
              this.loading = false;
              this.toggleSuccessModal(true);
            } else {
              console.error(
                "set_hybrid_livestream_settings fail",
                response.data
              );
              this.getHybridSettings();
              this.$hybridapi("showToast", this.$t("errors.general_error"));
            }
          },
          (error) => {
            console.error("set_hybrid_livestream_settings error", error);
            this.getHybridSettings();
            this.$hybridapi("showToast", this.$t("errors.general_error"));
          }
        );
    },
    makeLivestreamPrivate(allowedUserIds) {
      this.loading = true;

      if (!this.hybridSettings.active_livestream_id) {
        console.error("Missing active_livestream_id", this.hybridSettings);
        this.getHybridSettings();
        this.$hybridapi("showToast", this.$t("errors.general_error"));
        return;
      }
      if (!this.selectedGift) {
        console.error(
          "Missing selectedGift",
          this.hybridSettings,
          this.selectedGift
        );
        this.getHybridSettings();
        this.$hybridapi("showToast", this.$t("errors.general_error"));
        return;
      }

      this.$axios
        .post("/livestream/make_private", {
          livestream_id: this.hybridSettings.active_livestream_id,
          gift_id: this.selectedGift.gift_id,
          allowed_ids: allowedUserIds,
        })
        .then(
          (response) => {
            console.log("make_private", response.data);

            if (response.data.success) {
              this.loading = false;
              this.toggleSuccessModal(true);
              setTimeout(() => {
                this.close();
              }, 2000);
            } else {
              console.error("make_private fail", response.data);
              this.toggleInviteModal(false);
              this.getHybridSettings();
              this.$hybridapi("showToast", this.$t("errors.general_error"));
            }
          },
          (error) => {
            console.error("make_private error", error);
            this.toggleInviteModal(false);
            this.getHybridSettings();
            this.$hybridapi("showToast", this.$t("errors.general_error"));
          }
        );
    },
    async getInvites() {
      await invitePromise;

      const livestreamId = this.hybridSettings.active_livestream_id;
      const cursor = this.inviteCursor;

      invitePromise = this.$axios
        .post(
          "/user/get_invite_list",
          {
            livestream_id: livestreamId,
            next: cursor,
          },
          { signal: invitePromiseController.signal }
        )
        .then(
          (response) => {
            console.log("get_invite_list", response.data);

            if (response.data.gifters) {
              if (cursor == null) {
                invitePromiseInitialFail = false;
              }

              response.data.gifters.forEach((item) => {
                item.selected = false;
              });
              this.inviteGifters = _.uniqBy(
                [...this.inviteGifters, ...response.data.gifters],
                (item) => item.user.user_id
              );

              this.inviteCursor = response.data.meta.next;
            } else {
              console.error("get_invite_list missing gifters", response.data);
              if (cursor == null) {
                invitePromiseInitialFail = true;
              }
            }
          },
          (error) => {
            console.error("get_invite_list error", error);
            if (cursor == null) {
              invitePromiseInitialFail = true;
            }
          }
        );

      return invitePromise;
    },
    backButtonPressed() {
      console.log("backButtonPressed");
      this.close();
    },
    giftSelectModalDone(gift) {
      console.log("giftSelectModalDone", gift);
      this.selectedGift = gift;
      this.toggleGiftModal(false);
      if (!this.showModeSelect) {
        this.toggleInviteModal(true);
      }
    },
    inviteModalDone(userIds) {
      console.log("inviteModalDone", userIds);
      this.toggleInviteModal(false);
      if (this.showModeSelect) {
        const userIdSet = new Set(userIds);
        const deepClone = JSON.parse(JSON.stringify(this.inviteGifters)); // dirty clone
        deepClone.forEach((item) => {
          item.selected = userIdSet.has(item.user.user_id);
        });
        this.inviteGifters = _.orderBy(
          deepClone,
          ["selected", "diamonds"],
          ["desc", "desc"]
        );
      } else {
        this.makeLivestreamPrivate(userIds);
      }
    },
    successModalDone() {
      console.log("successModalDone");
      this.close();
    },
    changeGiftButton() {
      this.toggleGiftModal(true);
    },
    changeInvitesButton() {
      this.toggleInviteModal(true);
    },
    inviteModalLoadMore() {
      if (this.inviteCursor == null) {
        console.error("inviteModalLoadMore called with null cursor");
        return;
      }

      if (this.loading) return;
      if (this.loadingInvites) return;

      this.loadingInvites = true;
      this.getInvites().then(() => {
        this.loadingInvites = false;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
#hybrid_settings {
  background-color: black;
  min-height: 100vh;
  color: white;
  font-family: Avenir;
  user-select: none;
}

#main_page {
  background: url("../../assets/img/hybrid_settings/background.svg") no-repeat
    top;
  background-size: contain;
  min-height: 100vh;
}

#logo {
  background: url("../../assets/img/hybrid_settings/logo.svg") no-repeat center
    bottom;
  background-size: 128px 77px;
  height: 128px;
}
@media only screen and (min-height: 700px) {
  #logo {
    background-size: 140px 86px;
    height: 140px;
  }
}

#mode_select_layout {
  height: calc(100vh - 128px);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 14px;
  width: 100vw;
  overflow: hidden;
  .modeSelectSpacer {
    flex-grow: 1;
  }
}
@media only screen and (min-height: 700px) {
  #mode_select_layout {
    height: calc(100vh - 140px);
    gap: 20px;
  }
}

.userProfile {
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding: 20px 0 10px;
  gap: 10px;
  .userPhotoHolder {
    display: flex;
    justify-content: center;
    .userPhoto {
      width: 44px;
      height: 44px;
      border: solid 1px #fff;
      border-radius: 100%;
      overflow: hidden;
      img {
        width: 100%;
        height: 100%;
      }
    }
  }
  .userName {
    text-align: center;
    font-family: Avenir-Heavy;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: calc(100vw - 98px);
    line-height: 44px;
  }
}
@media only screen and (min-height: 700px) {
  .userProfile {
    flex-grow: 1;
    align-items: center;
    gap: 14px;
    .userPhotoHolder {
      .userPhoto {
        width: 60px;
        height: 60px;
      }
    }
    .userName {
      font-size: 18px;
      line-height: 60px;
    }
  }
}

.modeSelect {
  display: flex;
  justify-content: center;
  .modeOption {
    display: flex;
    width: 100px;
    height: 140px;
    border-radius: 15px;
    flex-direction: column;
    justify-content: center;
    padding: 0 22px;
    &.publicStream {
      margin-right: 12px;
      &.selected {
        padding: 0 20px; // for the border.
        border: solid 2px #0095ff;
        background-color: #252525;
      }
      .modeOptionIcon {
        width: 56px;
        height: 56px;
        background: url("../../assets/img/hybrid_settings/public.svg") no-repeat
          center bottom;
      }
    }
    &.privateStream {
      &.selected {
        background: url("../../assets/img/hybrid_settings/premium-option-box.svg")
          no-repeat center center;
      }
      .modeOptionIcon {
        width: 56px;
        height: 56px;
        background: url("../../assets/img/hybrid_settings/premium.svg")
          no-repeat center center;
      }
    }
    .modeOptionText {
      font-family: Avenir-Heavy;
      font-size: 12px;
      text-align: center;
      line-height: 1.17;
      letter-spacing: normal;
      text-align: center;
      margin-top: 11px;
    }
  }
}
@media only screen and (min-height: 700px) {
  .modeSelect {
    margin-bottom: 20px;
    transform: scale(1.2);
  }
}

.modeSelectGift {
  padding: 0 24px;
  .giftTitle {
    font-family: Avenir;
    font-size: 14px;
    line-height: 1;
    letter-spacing: normal;
    text-align: left;
    margin-bottom: 9px;
  }
  .giftWrapper {
    height: 60px;
    align-self: stretch;
    flex-grow: 0;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 14px;
    padding: 8px 20px;
    border-radius: 21px;
    background-color: rgba(44, 44, 44, 0.85);
    .giftImage {
      width: 65px;
      height: 65px;
      flex-grow: 0;
      object-fit: contain;
      border-radius: 12px;
    }
    .giftPrice {
      img {
        width: 16px;
        height: 16px;
        margin-bottom: -2px;
      }
      span {
        flex-grow: 0;
        font-family: Avenir;
        font-size: 15px;
        font-weight: 500;
        font-stretch: normal;
        font-style: normal;
        line-height: normal;
        letter-spacing: normal;
        text-align: left;
        color: #fff;
      }
    }
    .spacer {
      flex-grow: 1;
    }
    .changeButton {
      flex-grow: 0;
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      gap: 10px;
      padding: 10px 16px;
      border-radius: 26px;
      background-color: #717171;
    }
  }
}

.modeSelectInvites {
  padding: 0 24px;
  .invitesTitle {
    font-family: Avenir;
    font-size: 14px;
    line-height: 1;
    letter-spacing: normal;
    text-align: left;
    margin-bottom: 9px;
  }
  .invitesWrapper {
    align-self: stretch;
    flex-grow: 0;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 14px;
    padding: 8px 20px;
    border-radius: 21px;
    background-color: rgba(44, 44, 44, 0.85);
    .inviteImages {
      display: flex;
      padding-left: 12px;
      .inviteImage {
        width: 32px;
        height: 32px;
        border: solid 1px #fff;
        border-radius: 100%;
        overflow: hidden;
        margin-left: -12px;
        img {
          width: 100%;
          height: 100%;
        }
      }
    }
    .spacer {
      flex-grow: 1;
    }
    .changeButton {
      flex-grow: 0;
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      gap: 10px;
      padding: 10px 16px;
      border-radius: 26px;
      background-color: #717171;
    }
  }
}

.modeSelectGiftPlaceholder {
  height: 184px;
}

.modeSelectInfo {
  display: flex;
  justify-content: center;
  gap: 8px;
  padding: 0 24px;
  img {
    width: 18px;
    height: 18px;
  }
  .modeSelectInfoText {
    flex-grow: 0;
    font-family: Avenir-Heavy;
    font-size: 14px;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
    text-align: left;
    color: #fff;
  }
}

.modeSaveBox {
  padding: 0 54px max(44px, env(safe-area-inset-bottom));
  .modeSaveButton {
    flex-grow: 0;
    padding: 12px 76px 12px 75px;
    border-radius: 22px;
    background-color: #0095ff;
    text-align: center;
  }
}
@media only screen and (max-height: 700px) {
  .modeSaveBox {
    padding: 0 54px max(12px, env(safe-area-inset-bottom));
  }
}

#back_button {
  position: absolute;
  left: 19px;
  top: max(30px, calc(11px + env(safe-area-inset-top)));
  img {
    width: 15px;
  }
}
</style>
